/**
 * AnchorPricingTable — the anchors, as tier cards (APP-ENTITLEMENT-001 Phase 11): ParcelPuffin, PublishPuffin and
 * ProPuffin ("coming soon"), each priced from the ONE catalog, listing what it includes and the apps that come
 * free with it, with the ONE signup URL as its CTA. Renders through `<PricingTable>`, the ONE tier-card grid.
 *
 * @module @puffin-core/marketing-shell/pricing/AnchorPricingTable
 */

import PropTypes from 'prop-types';
import { anchorEntriesOf } from '@puffin-core/app-catalog';
import { useAccountAppsUiTranslate } from '@puffin-core/account-apps-ui/copy';

import PricingTable from '../components/PricingTable.jsx';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';
import { anchorTierOf } from './anchorTiers.js';

/**
 * @param {Object} props
 * @param {import('./pricingRules.js').PublicCatalog} props.catalog - the public catalog.
 * @param {string} props.siteAppKey - the app whose site this is.
 * @param {string} props.featuredAnchorKey - the anchor this site features (`featuredAnchorKeyOf`).
 * @param {string} props.billingCycle
 * @param {string} [props.fromOrigin] - this site's origin (`signupUrlFor`).
 * @param {(cents: number) => string} props.formatPrice
 * @param {Function} [props.t] - the host's translator, honoured first.
 * @param {(href: string, destinationKind: string) => void} [props.onOutboundClick] - handed to `<PricingTable>`.
 * @param {string} [props.id='anchors']
 * @returns {React.JSX.Element}
 */
export function AnchorPricingTable({
  catalog,
  siteAppKey,
  featuredAnchorKey,
  billingCycle,
  fromOrigin,
  formatPrice,
  t: hostT,
  onOutboundClick,
  id = 'anchors',
}) {
  const t = useMarketingShellTranslate(hostT);
  const tApps = useAccountAppsUiTranslate(hostT);
  const tiers = anchorEntriesOf(catalog).map((entry) =>
    anchorTierOf({
      catalog,
      entry,
      siteAppKey,
      featuredAnchorKey,
      billingCycle,
      fromOrigin,
      t,
      tApps,
      formatPrice,
    }),
  );
  return (
    <PricingTable
      id={id}
      heading={t('pricing.anchorsHeading')}
      subheading={t('pricing.anchorsSubheading')}
      tiers={tiers}
      onOutboundClick={onOutboundClick}
    />
  );
}

AnchorPricingTable.propTypes = {
  catalog: PropTypes.shape({ apps: PropTypes.arrayOf(PropTypes.object).isRequired }).isRequired,
  siteAppKey: PropTypes.string.isRequired,
  featuredAnchorKey: PropTypes.string.isRequired,
  billingCycle: PropTypes.string.isRequired,
  fromOrigin: PropTypes.string,
  formatPrice: PropTypes.func.isRequired,
  t: PropTypes.func,
  onOutboundClick: PropTypes.func,
  id: PropTypes.string,
};

export default AnchorPricingTable;
